import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    getProjectChangeRequests,
    createProjectChangeRequest,
    updateProjectChangeRequest,
} from '@services/projects.service';
import { useCanCheck } from '@/ability/helpers';
import {
    PROJECT_CHANGE_REQUESTS_VIEW,
    PROJECT_CHANGE_REQUESTS_CREATE,
    PROJECT_CHANGE_REQUESTS_RESOLVE,
} from '@/ability';
import { invalidateProjectQueries } from './projectQueryInvalidation';
import { useIsClientProfile } from '@hooks/useIsClientProfile';
import { useProjectDetail } from '@hooks/projects/detail/useProjectDetail';

export const PROJECT_CHANGE_REQUESTS_QUERY_KEY = (projectId, params) => ['projects', projectId, 'change-requests', params];

const DEFAULT_PARAMS = { page: 1, limit: 20 };

/**
 * Build API payload from form values (trim + defaults). Used so the tab does not contain formatting logic.
 */
export function buildChangeRequestPayload(values) {
    return {
        title: (values?.title ?? '').trim() || 'Change request',
        description: (values?.description ?? '').trim() || '',
    };
}

export function useProjectChangeRequests(projectId, params = DEFAULT_PARAMS) {
    const queryClient = useQueryClient();
    const can = useCanCheck();
    const isClientView = useIsClientProfile();
    const canView = can(PROJECT_CHANGE_REQUESTS_VIEW);
    const canCreate = can(PROJECT_CHANGE_REQUESTS_CREATE);
    const canResolve = can(PROJECT_CHANGE_REQUESTS_RESOLVE);
    const queryParams = { page: params?.page ?? 1, limit: params?.limit ?? 20 };
    const [pendingUpdateRequestId, setPendingUpdateRequestId] = React.useState(null);

    const { data: project } = useProjectDetail(projectId, { enabled: !!projectId });
    const isChangeRequestEnabled = project?.capabilities?.useTasks !== false && project?.capabilities?.clientFacingChangeAndSubmission !== false;

    const { data, isLoading, isError, refetch } = useQuery({
        queryKey: PROJECT_CHANGE_REQUESTS_QUERY_KEY(projectId, queryParams),
        queryFn: () => getProjectChangeRequests(projectId, queryParams),
        enabled: !!projectId && canView && !!isChangeRequestEnabled,
    });

    const list = data?.data ?? [];
    const total = data?.total ?? 0;
    const page = data?.page ?? 1;
    const limit = data?.limit ?? 20;
    const totalPages = data?.totalPages ?? 1;

    const createMutation = useMutation({
        mutationFn: (body) => createProjectChangeRequest(projectId, body),
        onSuccess: () => {
            invalidateProjectQueries(queryClient, projectId);
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ requestId, body }) => updateProjectChangeRequest(projectId, requestId, body),
        onSuccess: () => {
            invalidateProjectQueries(queryClient, projectId);
        },
        onSettled: () => {
            setPendingUpdateRequestId(null);
        },
    });

    const createChangeRequest = (body, callbacks = {}) => {
        createMutation.mutate(body, {
            onSuccess: callbacks.onSuccess,
            onError: callbacks.onError,
        });
    };

    const updateChangeRequest = (requestId, body, callbacks = {}) => {
        setPendingUpdateRequestId(requestId ?? null);
        updateMutation.mutate(
            { requestId, body },
            {
                onSuccess: callbacks.onSuccess,
                onError: callbacks.onError,
            }
        );
    };

    const resolveChangeRequest = (requestId, callbacks = {}) => {
        // Backend endpoint name is /resolve; status values are backend-defined (e.g. approved/declined).
        updateChangeRequest(requestId, { status: 'approved' }, callbacks);
    };

    /** Submit from form values; builds payload in hook so tab has no formatting logic. */
    const submitCreateChangeRequest = (formValues, callbacks = {}) => {
        createChangeRequest(buildChangeRequestPayload(formValues), callbacks);
    };

    return {
        isClientView,
        canView,
        canCreate,
        canResolve,
        changeRequests: Array.isArray(list) ? list : [],
        total,
        page,
        limit,
        totalPages,
        isLoading,
        isError,
        refetch,
        createChangeRequest,
        submitCreateChangeRequest,
        createChangeRequestPending: createMutation.isPending,
        updateChangeRequest,
        updateChangeRequestPending: updateMutation.isPending,
        pendingUpdateRequestId,
        resolveChangeRequest,
    };
}
